import styled from '@emotion/styled';
import { FileReference } from '@kaizenplatform/belfast-entity';
import { Typography } from '@material-ui/core';
import { createElement, FC, Fragment, MouseEventHandler, useEffect, useState } from 'react';
import { FormattedDate, FormattedHTMLMessage, FormattedMessage } from 'react-intl';

import CreateModificationRequest from '../../../../entity/CreateModificationRequest';
import EditModificationRequestItem from '../../../../entity/EditModificationRequestItem';
import Language from '../../../../entity/Language';
import ProductPricing from '../../../../entity/ProductPricing';
import UploadingFile from '../../../../entity/UploadingFile';
import {
  CreationDetailFragment,
  ModificationRequestFeeType,
  SingleDirectorUserFragment,
} from '../../../../generated/graphql';
import { getLatestDeliverable } from '../../../../graphql/creation';
import { toJSDate } from '../../../../graphql/datetime';
import theme from '../../../../theme';
import { calculateModificationRequestPoint } from '../../../../utility/calculatePoint';
import { SPACING_LARGE, SPACING_REGULAR } from '../../../constant/spacing';
import { gridLayoutContainer, gridLayoutItem } from '../../../styleUtility/gridLayout';
import mq from '../../../styleUtility/mediaquery';
import HelpAnchor from '../../compound/HelpAnchor';
import _RequiredPointSummary from '../../compound/RequiredPointSummary';
import Select, { SelectItem } from '../../compound/Select';
import _CreatingForm from './CreatingForm';
import ModificationRequestAlertFeeDialog from './ModificationRequestAlertFeeDialog';

interface Props {
  creation: CreationDetailFragment;
  createModificationRequest: CreateModificationRequest;
  editModificationRequestItem: EditModificationRequestItem;
  assetFiles: Array<FileReference>;
  onSelectFeeType: (feeType: ModificationRequestFeeType) => void;
  onUploadAssetFiles: (files: UploadingFile[]) => void;
  onPushInputtingItem: (
    modificationRequestId: string,
    bucketId: string | null,
    inputDuration: string,
    inputComment: string,
    files: Array<FileReference>,
  ) => Promise<void>;
  onRequestRemoveItem: (modificationRequestItemId: string, index: number) => void;
  onRemoveEditAssetFile: (fileId: string) => void;
  onOpenEditModificationRequestDialog: (index: number) => void;
  onCloseEditModificationRequestDialog: () => void;
  onSubmitEditModificationRequestDialog: (
    index: number,
    editModificationRequestItem: EditModificationRequestItem,
    modificationRequestItemId: string,
    duration: string,
    comment: string,
  ) => void;
  onRequestEditAssetFiles: (files: UploadingFile[], bucketId: string) => void;
  onRemoveAssetFile: (bucketId: string, fileId: string) => void;
  onRemoveAllAssetFile: () => void;
  onClickCancelButton: MouseEventHandler<HTMLElement>;
  onClickSendButton: () => Promise<void>;
  onRefetchCreation: () => void;
  className?: string;
  pointBalance: number | null;
  organizationId: string;
  isPointRequired: boolean;
  productPricing?: ProductPricing;
  email: string;
  language: Language;
  onPurchasedPoint: (point: number) => Promise<void>;
  prepareUser: () => Promise<SingleDirectorUserFragment | void>;
  draftModificationRequestDeliveryDue?: Date;
}

const ModificationRequestCreatingCard: FC<Props> = ({
  className,
  creation,
  createModificationRequest,
  editModificationRequestItem,
  assetFiles,
  onSelectFeeType,
  onUploadAssetFiles,
  onPushInputtingItem,
  onRequestRemoveItem,
  onRemoveEditAssetFile,
  onOpenEditModificationRequestDialog,
  onCloseEditModificationRequestDialog,
  onSubmitEditModificationRequestDialog,
  onRequestEditAssetFiles,
  onRemoveAssetFile,
  onRemoveAllAssetFile,
  onClickCancelButton,
  onClickSendButton,
  onRefetchCreation,
  pointBalance,
  isPointRequired,
  productPricing,
  organizationId,
  email,
  language,
  onPurchasedPoint,
  prepareUser,
  draftModificationRequestDeliveryDue,
}) => {
  const [isAlertFeeDialogOpen, setAlertFeeDialog] = useState<boolean>(false);

  useEffect(() => {
    onSelectFeeType(creation.isNextModificationRequestForAFee ? 'fee' : 'no_fee');
    // TODO: Implementation before introducing rules
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // 修正依頼入力中にブラウザを閉じる場合はalertを表示する
  useEffect(() => {
    const setDeleteConfirmAlert = (e: BeforeUnloadEvent) => {
      if (createModificationRequest.items.length > 0) {
        e.preventDefault(); // for IE and Edge
        let returnText;

        <FormattedMessage id="message.modification_request.close_browser">
          {(text: string) => (returnText = text as string)}
        </FormattedMessage>;

        e.returnValue = returnText;
      }
    };

    window.addEventListener('beforeunload', setDeleteConfirmAlert);

    return () => {
      return window.removeEventListener('beforeunload', setDeleteConfirmAlert);
    };
  }, [createModificationRequest.items.length]);

  const shortagePoints = pointBalance === null ? 0 : pointBalance - (calculateModificationRequestPoint(creation) || 0);

  return (
    <Root className={className}>
      <Description>
        <Meta variant="subtitle1">
          {createModificationRequest.feeType == 'no_fee' &&
          creation.modificationRequestFreeExpiredAt &&
          typeof creation.modificationRequestFreeLimit == 'number' ? (
            <FormattedDate
              value={toJSDate(creation.modificationRequestFreeExpiredAt)}
              year="numeric"
              month="2-digit"
              day="2-digit"
              hour="2-digit"
              minute="2-digit"
            >
              {(dateTime: string) =>
                isPointRequired ? (
                  <>
                    <FormattedMessage id="modification_request_creating_card.expire_message" />
                    <ExpireCount>
                      <FormattedMessage
                        id="modification_request_creating_card.expire_count"
                        values={{
                          count: creation.modificationRequestFreeLimit,
                        }}
                      />
                    </ExpireCount>
                    <FormattedMessage
                      id="modification_request_creating_card.expire_at"
                      values={{
                        dateTime: dateTime,
                      }}
                    />
                  </>
                ) : (
                  <FormattedMessage
                    id="modification_request_creating_card.expire_message_for_global"
                    values={{ dateTime }}
                  />
                )
              }
            </FormattedDate>
          ) : null}
        </Meta>

        {createModificationRequest.feeType === 'fee' ? (
          <Meta gutterBottom={true} color="error">
            <FormattedMessage id="modification_request_creating_card.for_a_fee" />
          </Meta>
        ) : null}

        <Typography gutterBottom={true}>
          <FormattedHTMLMessage id="modification_request_creating_card.description" />
        </Typography>

        <Typography>
          <FormattedMessage
            id="modification_request.help"
            values={{
              help_link: <HelpAnchor url="https://help.kaizen-ad.com/ja/articles/3637650">ヘルプページ</HelpAnchor>,
            }}
          />
        </Typography>

        {draftModificationRequestDeliveryDue && (
          <FormattedDate value={draftModificationRequestDeliveryDue} year="numeric" month="2-digit" day="2-digit">
            {(date: string) => (
              <Typography>
                <FormattedMessage id="modification_request_creating_card.delivery_due" values={{ date }} />
              </Typography>
            )}
          </FormattedDate>
        )}
      </Description>

      {isPointRequired && !creation.isNextModificationRequestForAFee ? (
        <FeeTypeDescription>
          <Select
            label={<FormattedMessage id="modification_request_creating_card.fee_type" />}
            defaultValue={'no_fee'}
            overrideValue={createModificationRequest.feeType ? createModificationRequest.feeType : undefined}
            onChange={(e) => {
              const selectedFeeType = e.target.value as ModificationRequestFeeType;
              onSelectFeeType(selectedFeeType);
              selectedFeeType === 'fee' ? setAlertFeeDialog(true) : setAlertFeeDialog(false);
            }}
          >
            <SelectItem value="fee">
              <FormattedMessage id="modification_request_creating_card.item_content.fee_type.fee" />
            </SelectItem>
            <SelectItem value="no_fee">
              <FormattedMessage id="modification_request_creating_card.item_content.fee_type.no_fee" />
            </SelectItem>
          </Select>
        </FeeTypeDescription>
      ) : null}

      {isPointRequired &&
      productPricing &&
      pointBalance !== null &&
      createModificationRequest.feeType === 'fee' &&
      typeof creation.modificationRequestPoint === 'number' ? (
        <PointDescription>
          <RequiredPointSummary
            currentPointBalance={pointBalance}
            pointTotal={calculateModificationRequestPoint(creation)}
            productPricing={productPricing}
          />
        </PointDescription>
      ) : null}

      <ModificationRequestAlertFeeDialog
        onClickAcceptButton={() => setAlertFeeDialog(false)}
        onRequestClose={() => {
          onSelectFeeType('no_fee');
          setAlertFeeDialog(false);
        }}
        open={isAlertFeeDialogOpen}
      />

      {createModificationRequest ? (
        <CreatingForm
          creation={creation}
          createModificationRequest={createModificationRequest}
          editModificationRequestItem={editModificationRequestItem}
          deliverable={getLatestDeliverable(creation)!}
          files={assetFiles}
          bucketId={createModificationRequest.bucketId}
          // Create
          uploadingAssetFiles={createModificationRequest.uploadingAssetFiles}
          uploadingFilesProgress={createModificationRequest.uploadingFilesProgress}
          onRequestUploadFiles={onUploadAssetFiles}
          onRemoveAssetFile={onRemoveAssetFile}
          onRemoveAllAssetFile={onRemoveAllAssetFile}
          onPushInputtingItem={onPushInputtingItem}
          onRequestRemoveItem={onRequestRemoveItem}
          // Edit
          onRemoveEditAssetFile={onRemoveEditAssetFile}
          onOpenEditModificationRequestDialog={onOpenEditModificationRequestDialog}
          onCloseEditModificationRequestDialog={onCloseEditModificationRequestDialog}
          onSubmitEditModificationRequestDialog={onSubmitEditModificationRequestDialog}
          onRequestEditAssetFiles={onRequestEditAssetFiles}
          onClickCancelButton={onClickCancelButton}
          onClickSendButton={onClickSendButton}
          onRefetchCreation={onRefetchCreation}
          isPointRequired={isPointRequired}
          productPricing={productPricing || undefined}
          shortagePoints={shortagePoints}
          organizationId={organizationId}
          language={language}
          email={email}
          onPurchasedPoint={onPurchasedPoint}
          prepareUser={prepareUser}
        />
      ) : null}
    </Root>
  );
};

const Root = styled('div')(
  gridLayoutContainer({
    columns: ['1fr', 'auto', '200px'],
    rows: ['auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto', 'auto'],
    areas: [
      ['description', 'description', 'description'],
      ['feeTypeDescription', 'pointDescription', 'pointDescription'],
      ['creatingForm', 'creatingForm', 'creatingForm'],
      ['buttons', 'buttons', 'buttons'],
    ],
  }),
);

const Description = styled('div')(gridLayoutItem('description'), {
  marginBottom: SPACING_LARGE,
});

const FeeTypeDescription = styled('div')(gridLayoutItem('feeTypeDescription'), {});

const CreatingForm = styled(_CreatingForm)(gridLayoutItem('creatingForm'), {
  marginBottom: SPACING_REGULAR,
});

const RequiredPointSummary = styled(_RequiredPointSummary)({
  padding: 0,
});

const PointDescription = styled('div')(gridLayoutItem('pointDescription'), { marginBottom: SPACING_REGULAR });

const Meta = styled(Typography)(
  gridLayoutItem('expiredAt', {
    alignSelf: 'flex-end',
  }),
  {
    marginBottom: SPACING_REGULAR,
    fontWeight: 'bold',
  },
  {
    [mq('sp')]: {
      whiteSpace: 'initial',
      wordBreak: 'break-word',
    },
  },
);

const ExpireCount = styled('span')({
  color: theme.palette.primary.main,
  fontWeight: 'bold',
});

export default ModificationRequestCreatingCard;
